import { z } from 'zod';

export const PlaylistVideoSchema = z.object({
  id: z.string().uuid(),
  playlistId: z.string().uuid(),
  youtubeVideoId: z.string(),
  position: z.number().int(),
  durationSeconds: z.number().int(),
  title: z.string().optional(), // Optional metadata from YouTube
  thumbnailUrl: z.string().optional(), // Optional metadata from YouTube
  score: z.number().int().optional(),
  userVote: z.union([z.literal(-1), z.literal(1)]).nullable().optional(),
});

export type PlaylistVideo = z.infer<typeof PlaylistVideoSchema>;

export const SuggestionStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type SuggestionStatus = z.infer<typeof SuggestionStatusSchema>;

export const SuggestionSchema = z.object({
  id: z.string().uuid(),
  playlistId: z.string().uuid(),
  suggesterId: z.string().uuid(),
  suggesterUsername: z.string().optional(),
  youtubeVideoId: z.string(),
  title: z.string(),
  channelTitle: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  durationSeconds: z.number().int(),
  status: SuggestionStatusSchema,
  score: z.number().int(),
  voteCount: z.number().int().nonnegative().optional(),
  userVote: z.union([z.literal(-1), z.literal(1)]).nullable().optional(),
  createdAt: z.union([z.string().datetime(), z.date()]),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;

export const CreateSuggestionSchema = z.object({
  youtubeVideoId: z.string().min(1),
});
export type CreateSuggestionDto = z.infer<typeof CreateSuggestionSchema>;

export const SuggestionVoteSchema = z.object({
  voteValue: z.union([z.literal(-1), z.literal(1)]),
});
export type SuggestionVoteDto = z.infer<typeof SuggestionVoteSchema>;

export const PlaylistSchema = z.object({
  id: z.string().uuid(),
  shortId: z.string(),
  slug: z.string(),
  ownerId: z.string().uuid(),
  owner: z
    .object({
      id: z.string(),
      username: z.string(),
    })
    .optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  isPublic: z.boolean().default(false),
  hasChat: z.boolean().default(true),
  hasNowPlaying: z.boolean().default(true),
  allowSuggestions: z.boolean().default(false),
  liveUserCount: z.number().int().nonnegative().optional(),
  imagePath: z.string().optional(),
  playbackStartedAt: z.string().datetime().nullable(), // Null if no videos
  updatedAt: z.string().datetime(),
  videos: z.array(PlaylistVideoSchema).optional(),
  currentVideo: z.string().optional(),
});

export type Playlist = z.infer<typeof PlaylistSchema>;

export const PlaylistListItemSchema = PlaylistSchema.extend({
  thumbnailUrl: z.string().optional(),
  owner: z
    .object({
      id: z.string(),
      username: z.string(),
      avatarPath: z.string().nullable().optional(),
    })
    .optional(),
});

export type PlaylistListItem = z.infer<typeof PlaylistListItemSchema>;

// DTOs
export const CreatePlaylistSchema = z.object({
  title: z.string().min(1),
  description: z.string().optional(),
  isPublic: z.coerce.boolean().optional(),
  hasChat: z.coerce.boolean().optional(),
  allowSuggestions: z.coerce.boolean().optional(),
  videos: z.array(z.string()).optional(),
});

export type CreatePlaylistDto = z.infer<typeof CreatePlaylistSchema>;

export const AddVideoSchema = z.object({
  youtubeVideoId: z.string().min(1),
});

export type AddVideoDto = z.infer<typeof AddVideoSchema>;

export const UpdatePlaylistSchema = z.object({
  title: z.string().min(1).optional(),
  description: z.string().optional(),
  isPublic: z.coerce.boolean().optional(),
  hasChat: z.coerce.boolean().optional(),
  hasNowPlaying: z.coerce.boolean().optional(),
  allowSuggestions: z.coerce.boolean().optional(),
  videos: z.array(z.string()).optional(),
});

export type UpdatePlaylistDto = z.infer<typeof UpdatePlaylistSchema>;

export const BatchAddVideosSchema = z.object({
  youtubeVideoIds: z.array(z.string().min(1)),
});

export type BatchAddVideosDto = z.infer<typeof BatchAddVideosSchema>;

export const ReorderVideosSchema = z.object({
  videoIds: z.array(z.string().uuid()),
});

export type ReorderVideosDto = z.infer<typeof ReorderVideosSchema>;

export const BatchRemoveVideosSchema = z.object({
  videoIds: z.array(z.string().uuid()),
});

export type BatchRemoveVideosDto = z.infer<typeof BatchRemoveVideosSchema>;

export const ImportYoutubePlaylistSchema = z.object({
  youtubePlaylistId: z.string().min(1),
});

export type ImportYoutubePlaylistDto = z.infer<
  typeof ImportYoutubePlaylistSchema
>;

export const OmittedVideoSchema = z.object({
  youtubeVideoId: z.string(),
  reason: z.enum([
    'not_embeddable',
    'private',
    'deleted',
    'region_restricted',
    'unprocessed',
  ]),
  allowedCountryCount: z.number().int().optional(),
  blockedCountryCount: z.number().int().optional(),
  title: z.string().optional(),
  thumbnailUrl: z.string().optional(),
});

export type OmittedVideo = z.infer<typeof OmittedVideoSchema>;

export const ImportPlaylistResultSchema = z.object({
  playlist: PlaylistSchema,
  regionRestricted: z.array(OmittedVideoSchema),
  notPlayable: z.array(OmittedVideoSchema),
});

export type ImportPlaylistResult = z.infer<typeof ImportPlaylistResultSchema>;

// WebSocket Events
export enum PlaylistEvents {
  UPDATED = 'playlist_updated',
  RESET = 'playlist_reset',
  VIDEO_ADDED = 'playlist_video_added',
  VIDEO_REMOVED = 'playlist_video_removed',
  REORDERED = 'playlist_reordered',
  TRACK_STARTED = 'playlist_start_track',
  COUNT_UPDATED = 'playlist_count_updated',
  SUGGESTION_ADDED = 'suggestion_added',
  SUGGESTION_VOTE_UPDATED = 'suggestion_vote_updated',
  SUGGESTION_MODERATED = 'suggestion_moderated',
  VIDEO_VOTE_UPDATED = 'video_vote_updated',
}

export interface PlaylistState {
  playlistId: string;
  currentVideoId: string | null;
  currentVideoPk?: string | null;
  currentVideoTitle?: string | null;
  currentVideoChannelTitle?: string | null;
  currentVideoOffset: number; // Seconds
  isPlaying: boolean; // Always true in this model, but kept for compatibility/extensibility
}

export const PlaylistsResponseSchema = z.object({
  items: z.array(PlaylistListItemSchema),
  meta: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
  }),
});

export type PlaylistsResponse = z.infer<typeof PlaylistsResponseSchema>;
